import "./CopyButton.scss";

import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaCheck } from "react-icons/fa";

import Button from "../Button/Button";
import Copy from "./copy.svg?react";

interface CopyButtonProps {
  message: string;
  className?: string;
  ariaLabel?: string;
  label?: string;
  width?: number | string;
  height?: number;
  reverse?: boolean;
  onClick?: () => void;
}

/** How long the button stays in its "copied" state after a successful write. */
const COPIED_FEEDBACK_MS = 1100;

/**
 * Copies through the legacy selection API.
 *
 * Kept as a fallback because `navigator.clipboard` only exists in a secure
 * context: an app opened over plain http — a phone on the LAN hitting a dev
 * server, an intranet host — has no async clipboard at all, and the copy used
 * to fail there with nothing to fall back on.
 */
const copyWithSelection = (message: string): boolean => {
  const textarea = document.createElement("textarea");
  textarea.value = message;
  textarea.setAttribute("readonly", "");
  // Out of sight but still selectable. `fixed` at the top-left keeps the page
  // from scrolling when the field takes focus.
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);

  // Copying steals the focus; hand it back so a keyboard user does not lose
  // their place on the button they just pressed.
  const previouslyFocused = document.activeElement;

  try {
    if (/ipad|iphone|ipod/i.test(navigator.userAgent)) {
      // WebKit ignores `select()` on a readonly field: the only combination it
      // honours is an editable node whose contents are selected through a Range.
      textarea.contentEditable = "true";
      textarea.readOnly = false;
      const range = document.createRange();
      range.selectNodeContents(textarea);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      textarea.setSelectionRange(0, message.length);
    } else {
      textarea.focus();
      textarea.select();
    }

    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();

    if (previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }
  }
};

/**
 * Writes to the clipboard, preferring the async API and falling back to the
 * selection copy when it is missing or refuses (denied permission, a document
 * that lost focus).
 */
const writeToClipboard = async (message: string): Promise<boolean> => {
  if (typeof navigator.clipboard?.writeText === "function") {
    try {
      await navigator.clipboard.writeText(message);
      return true;
    } catch {
      // Falls through to the selection copy below.
    }
  }

  return copyWithSelection(message);
};

const CopyButton: React.FC<CopyButtonProps> = ({
  message,
  className,
  ariaLabel = "Copy to clipboard",
  label,
  width = label ? undefined : 24,
  height = label ? undefined : 24,
  reverse = false,
  onClick,
}) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    },
    [],
  );

  const copyToClipboard = async () => {
    // Copy first: both clipboard paths ride on the user gesture that is still
    // active on this tick, and an `onClick` awaiting anything (a fetch, a state
    // flush) would spend that activation before the write ever happens.
    const didCopy = await writeToClipboard(message);

    if (onClick) await onClick();

    if (!didCopy) {
      toast.error("Failed to copy to clipboard");
      return;
    }

    setCopied(true);

    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
    }
    resetTimer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <>
      <div
        className={`${className ?? ""} copyButton`}
        // Generic by default: callers must explicitly opt in before exposing
        // a non-secret copied value through the accessibility tree.
        aria-label={ariaLabel}
        role="button"
      >
        <Button
          onClick={copyToClipboard}
          className={`toolsBarCopyButton ${copied ? "active" : ""} ${
            reverse ? "reverse" : ""
          }`}
          // The check lives inside the button on purpose: the "Copied" bubble
          // below is absolutely positioned, so any host with `overflow: hidden`
          // (a table wrapper, a card) clips it and the copy looks like it did
          // nothing. The icon swap is feedback nothing can crop.
          icon={copied ? <FaCheck /> : <Copy />}
          label={label}
          style={label ? "transparent" : "square"}
          height={height ?? "auto"}
          width={width}
          padding="0"
        />
        {copied && <div className="copiedMessage active">Copied</div>}
      </div>
    </>
  );
};

export default CopyButton;
